import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  Link,
  Form,
  useLocation,
  useMatches,
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react";
import { json } from "@remix-run/node";
import { useState, useEffect } from "react";
import { getUser } from "~/utils/session.server";
import { userHasPermission, userHasApprovedBusiness } from "~/utils/business.server";
import MobileNav from "~/components/MobileNav";
import AdminMenu from "~/components/AdminMenu";
import JobsMenu from "~/components/JobsMenu";
import MarineMenu from "~/components/MarineMenu";
import AccountMenu from "~/components/AccountMenu";
import NotificationListener, { NotificationToast } from "~/components/NotificationListener";
import OfflineNotification from "~/components/OfflineNotification";
import ImpersonationBanner from "~/components/ImpersonationBanner";
import NotificationPopup from "~/components/NotificationPopup";
import DebugWidget from "~/components/DebugWidget";
import { registerServiceWorker } from "./utils/offline.client";
// Import debug.client.ts early to ensure request interception is set up before anything loads
import "./utils/debug.client";

// Import tailwind CSS directly
import "./tailwind.css";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: "/build/tailwind.css" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700&display=swap" }
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUser(request);

  // Check permissions for AccountMenu if user is logged in
  let accountPermissions = {
    canAccessLicenseInfo: false,
    canAccessBusinessManagement: false
  };

  if (user && user.id) {
    // Special case for site_admin - they can access everything
    if (user.role === "site_admin" || user.accountType === "site_admin") {
      accountPermissions.canAccessLicenseInfo = true;
      accountPermissions.canAccessBusinessManagement = true;
    } else {
      // Check license info permission
      try {
        accountPermissions.canAccessLicenseInfo = await userHasPermission(user.id, "view_license_info");
      } catch (error) {
        console.error("Error checking license permission:", error);
      }

      // Check business management permissions
      try {
        const hasEmployeePermission = await userHasPermission(user.id, "manage_employees");
        const hasRolePermission = await userHasPermission(user.id, "manage_roles");
        const hasBusinessSettingsPermission = await userHasPermission(user.id, "manage_business_settings");
        
        // Check if user has an approved business
        const hasApprovedBusiness = await userHasApprovedBusiness(user.id);

        // Only allow access to business management features if the user has both:
        // 1. The necessary permissions
        // 2. An approved business
        accountPermissions.canAccessBusinessManagement = 
          (hasEmployeePermission || hasRolePermission || hasBusinessSettingsPermission) && hasApprovedBusiness;
      } catch (error) {
        console.error("Error checking business permissions:", error);
      }
    }
  }

  return json({
    user,
    accountPermissions
  });
}

export function ErrorBoundary() {
  const error = useRouteError();
  const [showDebugWidget, setShowDebugWidget] = useState(false);

  // Check if the user is a site admin using localStorage
  useEffect(() => {
    // Try to determine if user is admin from localStorage
    const checkAdminStatus = () => {
      try {
        // Check if there's a debug flag in localStorage (set in the App component)
        const isAdmin = localStorage.getItem('ferrypros_is_admin') === 'true';
        setShowDebugWidget(isAdmin);
      } catch (e) {
        // If localStorage is not available, we can't determine admin status
        console.error("Could not access localStorage:", e);
      }
    };
    
    checkAdminStatus();
  }, []);

  // Log the error for debugging purposes
  console.error("Application error:", error);

  // Prepare a user-friendly error message
  let errorMessage = "An unexpected error occurred. Please try again later.";
  let errorDetails = "";
  let statusCode = 500;

  // Extract more specific information based on the error type
  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    errorMessage = `${error.status} - ${error.statusText}`;
    errorDetails = error.data?.message || error.data;
  } else if (error instanceof Error) {
    errorMessage = "Application Error";
    // In production, we might want to hide the stack trace
    errorDetails = process.env.NODE_ENV === "development" 
      ? `${error.message}\n\n${error.stack}` 
      : error.message;
  }

  captureRemixErrorBoundaryError(error);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <title>Error - FerryPros</title>
      </head>
      <body className="bg-gray-50">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
            <div className="mb-6">
              <svg className="h-16 w-16 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h1 className="text-2xl font-bold text-gray-800 mt-4">{errorMessage}</h1>
            </div>
            
            {errorDetails && (
              <div className="mb-6 text-left">
                <h2 className="text-lg font-medium text-gray-700 mb-2">Error Details:</h2>
                <div className="bg-gray-100 p-4 rounded-md overflow-auto max-h-60 text-sm font-mono whitespace-pre-wrap text-gray-700">
                  {errorDetails}
                </div>
              </div>
            )}
            
            <div className="flex flex-col space-y-4">
              <button 
                onClick={() => window.location.reload()} 
                className="w-full py-2 px-4 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Try Again
              </button>
              <Link 
                to="/"
                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
        
        {/* Debug Widget - only visible to site admins */}
        {showDebugWidget && <DebugWidget />}
        
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const { user, accountPermissions } = useLoaderData<typeof loader>();
  const location = useLocation();
  const matches = useMatches();
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [showSplash, setShowSplash] = useState(true);
  
  // Check if current route is an API route
  const isApiRoute = matches.some(match => match.id.startsWith('routes/api'));
  
  // Store admin status in localStorage for error boundary to use
  useEffect(() => {
    if (typeof window !== 'undefined' && user) {
      try {
        const isAdmin = user.role === "site_admin" || user.accountType === "site_admin";
        localStorage.setItem('ferrypros_is_admin', isAdmin ? 'true' : 'false');
      } catch (e) {
        console.error("Could not access localStorage:", e);
      }
    }
  }, [user]);

  // Handle splash screen - only on client side to avoid hydration mismatch
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initially set showSplash to match what was rendered on the server
      // This ensures hydration completes successfully
      setShowSplash(true);
      
      // Then after a short delay (after hydration), hide the splash screen
      const timer = setTimeout(() => {
        setShowSplash(false);
      }, 1500); // Show splash for 1.5 seconds to ensure hydration completes first

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  // Register service worker for offline capability
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Register service worker
      registerServiceWorker();

      // Listen for service worker update available event
      const handleUpdateAvailable = (event) => {
        setUpdateAvailable(true);
        setSwRegistration(event.detail);
      };

      window.addEventListener('serviceWorkerUpdateAvailable', handleUpdateAvailable);

      return () => {
        window.removeEventListener('serviceWorkerUpdateAvailable', handleUpdateAvailable);
      };
    }
  }, []);

  // Reset current notification when route changes
  useEffect(() => {
    setCurrentNotification(null);
  }, [location.pathname]);

  // Handle new notifications
  const handleNewNotification = (notification) => {
    setCurrentNotification(notification);
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setCurrentNotification(null);
  };

  // Handle service worker update
  const handleUpdate = () => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setUpdateAvailable(false);
  };

  // For API routes, only render the minimal necessary components
  if (isApiRoute) {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <Meta />
          <Links />
        </head>
        <body>
          <Outlet />
          <ScrollRestoration />
          <Scripts />
          <LiveReload />
        </body>
      </html>
    );
  }

  // For regular routes, render the full UI
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className={user ? "logged-in" : ""}>
        {/* Animated Splash Screen - Only show for logged-in users */}
        {showSplash && user && (
          <div 
            id="splash-screen"
            className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-r from-gray-800 to-gray-900"
            style={{ 
              animation: 'fadeIn 0.5s ease-in',
              animationFillMode: 'forwards'
            }}
          >
            <div className="text-center">
              <div className="mb-8">
                <img 
                  src="/images/logo.png" 
                  alt="FerryPros Logo" 
                  className="h-16 w-16 mx-auto mb-4 animate-pulse" 
                />
                <h1 className="text-4xl font-bold text-white mb-2">FerryPros</h1>
                <p className="text-gray-300">Connecting Aircraft Owners with Trusted Pilots</p>
              </div>
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            </div>
          </div>
        )}
        {user?.isImpersonating && (
          <ImpersonationBanner 
            actualRole={user.actualRole} 
            impersonatedRole={user.role}
            actualAccountType={user.actualAccountType}
            impersonatedAccountType={user.accountType}
          />
        )}
        <header className="bg-white text-gray-800 py-4 shadow-soft border-b border-gray-200">
          <div className="container-custom flex justify-between items-center">
            <h1 className="text-2xl font-display font-bold">
              <Link to={user ? "/dashboard" : "/" } className="hover:text-gray-700 transition-colors duration-200 flex items-center">
                <img src="/images/logo.png" alt="FerryPros Logo" className="h-6 w-6 mr-2" />
                FerryPros
              </Link>
            </h1>

            {/* Mobile Navigation */}
            <MobileNav user={user} accountPermissions={accountPermissions}/>

            {/* Desktop Navigation */}
            <div className="hidden md:flex flex-grow justify-end items-center">
              {/* Main Navigation */}
              <nav className="mr-6">
                <ul className="flex space-x-6 items-center">
                  {user ? (
                    <>
                      <li><Link to="/dashboard"
                                className="hover:text-gray-700 transition-colors duration-200 font-medium">Dashboard</Link>
                      </li>
                      <li className="relative">
                        <JobsMenu userRole={user.role} userAccountType={user.accountType} />
                      </li>
                      <li className="relative">
                        <MarineMenu userRole={user.role} userAccountType={user.accountType} />
                      </li>
                      <li className="relative">
                        <AccountMenu 
                          userRole={user.role} 
                          userAccountType={user.accountType} 
                          userId={user.id}
                          canAccessLicenseInfo={accountPermissions.canAccessLicenseInfo}
                          canAccessBusinessManagement={accountPermissions.canAccessBusinessManagement}
                        />
                      </li>
                      {(user.role === "site_admin" || user.accountType === "site_admin") && (
                        <li className="relative">
                          <AdminMenu />
                        </li>
                      )}
                    </>
                  ) : (
                    <>
                      <li><Link to="/" className="hover:text-gray-700 transition-colors duration-200 font-medium">Home</Link></li>
                      <li><Link to="/jobs" className="hover:text-gray-700 transition-colors duration-200 font-medium">Jobs</Link></li>
                      <li><Link to="/about" className="hover:text-gray-700 transition-colors duration-200 font-medium">About</Link></li>
                      <li><Link to="/contact" className="hover:text-gray-700 transition-colors duration-200 font-medium">Contact</Link></li>
                      <li><Link to="/login" className="hover:text-gray-700 transition-colors duration-200 font-medium">Login</Link></li>
                      <li><Link to="/notification-signup" className="btn-primary ml-2">Get Notified</Link></li>
                    </>
                  )}
                </ul>
              </nav>

              {/* Right Side Icons */}
              {user && (
                <div className="flex items-center space-x-4">
                  {/* Messages */}
                  <Link 
                    to="/messages/inbox" 
                    className="relative hover:text-gray-700 transition-colors duration-200 flex items-center text-gray-600"
                    aria-label="Messages"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </Link>

                  {/* Notifications */}
                  <Link 
                    to="/notifications" 
                    className="relative hover:text-gray-700 transition-colors duration-200 flex items-center text-gray-600"
                    aria-label="Notifications"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-gray-700 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-sm">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="min-h-screen">
          <Outlet />
        </main>
        <footer className="bg-gray-100 text-gray-800 py-12 border-t border-gray-200">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-xl mb-4 text-gray-800 flex items-center">
                  <img src="/images/logo.png" alt="FerryPros Logo" className="h-6 w-6 mr-2" />
                  FerryPros
                </h3>
                <p className="text-gray-600 mb-4">
                  Connecting aircraft owners and brokers with trusted pilots and ferry pilot agencies through a fully managed and transparent platform.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-4">Services</h3>
                <ul className="space-y-2 text-gray-600">
                  <li><Link to="/services/aircraft-ferry" className="hover:text-gray-700 transition-colors duration-200">Aircraft Ferry</Link></li>
                  <li><Link to="/services/pilot-verification" className="hover:text-gray-700 transition-colors duration-200">Pilot Verification</Link></li>
                  <li><Link to="/services/secure-escrow" className="hover:text-gray-700 transition-colors duration-200">Secure Escrow</Link></li>
                  <li><Link to="/services/flight-tracking" className="hover:text-gray-700 transition-colors duration-200">Flight Tracking</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-4">Company</h3>
                <ul className="space-y-2 text-gray-600">
                  <li><Link to="/about" className="hover:text-gray-700 transition-colors duration-200">About Us</Link></li>
                  <li><Link to="/contact" className="hover:text-gray-700 transition-colors duration-200">Contact</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-4">Legal</h3>
                <ul className="space-y-2 text-gray-600">
                  <li><Link to="/privacy-policy" className="hover:text-gray-700 transition-colors duration-200">Privacy Policy</Link></li>
                  <li><Link to="/terms-of-service" className="hover:text-gray-700 transition-colors duration-200">Terms of Service</Link></li>
                  <li><Link to="/cookie-policy" className="hover:text-gray-700 transition-colors duration-200">Cookie Policy</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-300 mt-8 pt-8 text-center text-gray-500">
              <p>&copy; 2025 FerryPros - All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Real-time notifications */}
        {user && (
          <NotificationListener 
            userId={user.id} 
            onNewNotification={handleNewNotification}
            onUnreadCountChange={setUnreadCount}
          />
        )}

        {/* Notification toast */}
        <NotificationToast 
          notification={currentNotification} 
          onClose={handleCloseNotification} 
        />

        {/* Offline notification */}
        <OfflineNotification />

        {/* Update notification */}
        {updateAvailable && (
          <div className="fixed bottom-4 left-4 right-4 md:left-auto md:w-80 p-4 bg-white border border-gray-200 rounded-lg shadow-soft z-50">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
              </div>
              <div className="ml-3 w-0 flex-1 pt-0.5">
                <p className="text-sm font-medium text-gray-800">
                  Update available
                </p>
                <p className="mt-1 text-sm text-bluegray-600">
                  A new version of the app is available.
                </p>
                <div className="mt-2">
                  <button
                    onClick={handleUpdate}
                    className="text-sm text-gray-700 font-medium hover:text-gray-800 transition-colors duration-200"
                  >
                    Update now
                  </button>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  className="bg-transparent rounded-md inline-flex text-gray-600 hover:text-gray-800 transition-colors duration-200 focus:outline-none"
                  onClick={() => setUpdateAvailable(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Popup for new visitors */}
        {!user && <NotificationPopup />}

        {/* Debug Widget - only visible to site admins */}
        {user && (user.role === "site_admin" || user.accountType === "site_admin") && (
          <DebugWidget />
        )}

        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>
  );
}

export default withSentry(App);